import React from "react";
import style from "../../calculator.scss";
import liabilityStyle from "./liabilities-section.scss";
import { UseFormTools } from "@wisr/common";
import { Serviceability } from "../../calculator";

import { Field, Icon } from "@wisr/react-ui";
import { FormValuesLabels } from "../../../../types/common-values";
import { DebtType, FREQUENCY_OPTIONS } from "../../calculator.consts";

export const existingLiabilitiesCount = (
  formData: UseFormTools<Serviceability>["formData"]
) => {
  for (let i = 1; i < 100; i++) {
    if (
      formData[`financialDebtType_${i}`] === undefined ||
      formData[`financialDebtType_${i}`] === ""
    ) {
      return i - 1;
    }
  }

  /* istanbul ignore next */
  return 0;
};

export const removeLiability = (
  formData: UseFormTools<Serviceability>["formData"],
  setValue: UseFormTools<Serviceability>["setValue"],
  liabilityIndex: number,
  liabilitiesLength: number,
  setLiabilitiesLength: (v: number) => void
) => {
  if (liabilityIndex !== liabilitiesLength) {
    for (let i = liabilityIndex; i < liabilitiesLength; i++) {
      setValue(
        `financialDebtType_${i}`,
        formData[`financialDebtType_${i + 1}`]
      );
      setValue(
        `financialDebtRepaymentAmount_${i}`,
        formData[`financialDebtRepaymentAmount_${i + 1}`]
      );
      setValue(
        `financialDebtRepaymentFrequency_${i}`,
        formData[`financialDebtRepaymentFrequency_${i + 1}`]
      );
      setValue(
        `financialDebtIsJointLoan_${i}`,
        formData[`financialDebtIsJointLoan_${i + 1}`]
      );
      setValue(
        `financialDebtIsInvestment_${i}`,
        formData[`financialDebtIsInvestment_${i + 1}`]
      );
      setValue(
        `financialDebtCreditCardLimit_${i}`,
        formData[`financialDebtCreditCardLimit_${i + 1}`]
      );
      setValue(
        `financialDebtConsolidate_${i}`,
        formData[`financialDebtConsolidate_${i + 1}`]
      );
      setValue(
        `financialDebtCloseThisCard_${i}`,
        formData[`financialDebtCloseThisCard_${i + 1}`]
      );
    }
  }

  setValue(`financialDebtType_${liabilitiesLength}`, "");
  setValue(`financialDebtRepaymentAmount_${liabilitiesLength}`, "");
  setValue(`financialDebtRepaymentFrequency_${liabilitiesLength}`, "");
  setValue(`financialDebtIsJointLoan_${liabilitiesLength}`, "");
  setValue(`financialDebtIsInvestment_${liabilitiesLength}`, "");
  setValue(`financialDebtCreditCardLimit_${liabilitiesLength}`, "");
  setValue(`financialDebtConsolidate_${liabilitiesLength}`, "");
  setValue(`financialDebtCloseThisCard_${liabilitiesLength}`, "");

  setLiabilitiesLength(liabilitiesLength - 1);
};

const creditCardField = (
  fieldAdapter: UseFormTools<Serviceability>["fieldAdapter"],
  liabilityIndex: number,
  condition: boolean
) => {
  return (
    <div className="input-column credit-card">
      <style jsx>{style}</style>
      <div className="input-row">
        <div className="left">
          <Field
            type="currency"
            label="Credit card limit"
            {...fieldAdapter({
              name: `financialDebtCreditCardLimit_${liabilityIndex}`,
              min: [1, "Credit card limit cannot be $0"],
              condition,
            })}
          />
        </div>
        <div className="right">
          <Field
            type="radio-button"
            label="Close this account?"
            options={["Yes", "No"]}
            {...fieldAdapter({
              name: `financialDebtCloseThisCard_${liabilityIndex}`,
              condition: condition,
            })}
          />
        </div>
      </div>
    </div>
  );
};

const personalLoanField = (
  fieldAdapter: UseFormTools<Serviceability>["fieldAdapter"],
  liabilityIndex: number,
  condition: boolean,
  isJoint: boolean
) => {
  return (
    <div className="input-column personal-loan">
      <style jsx>{style}</style>
      <div className="input-row">
        <Field
          type="currency"
          label="Repayment amount"
          {...fieldAdapter({
            name: `financialDebtRepaymentAmount_${liabilityIndex}`,
            min: [1, "Loan repayment cannot be $0"],
            condition,
          })}
        />
        <Field
          label="Frequency"
          type="select"
          options={FREQUENCY_OPTIONS}
          placeholder="Select..."
          {...fieldAdapter({
            name: `financialDebtRepaymentFrequency_${liabilityIndex}`,
            condition,
          })}
        />
      </div>
      <div className="input-row">
        <div className="left">
          <Field
            type="radio-button"
            label="Consolidate and close this loan?"
            options={["Yes", "No"]}
            {...fieldAdapter({
              name: `financialDebtConsolidate_${liabilityIndex}`,
              condition: condition,
            })}
          />
        </div>
        <div className="right">
          {!isJoint && (
            <Field
              type="radio-button"
              label="Is this a joint loan?"
              options={["Yes", "No"]}
              {...fieldAdapter({
                name: `financialDebtIsJointLoan_${liabilityIndex}`,
                condition: condition && !isJoint,
              })}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const mortgageField = (
  fieldAdapter: UseFormTools<Serviceability>["fieldAdapter"],
  liabilityIndex: number,
  condition: boolean,
  isJoint: boolean
) => {
  return (
    <div className="input-column mortgage">
      <style jsx>{style}</style>
      <div className="input-row">
        <Field
          type="currency"
          label="Total repayment amount"
          hint="Principal and interest"
          {...fieldAdapter({
            name: `financialDebtRepaymentAmount_${liabilityIndex}`,
            min: [1, "Loan repayment cannot be $0"],
            condition,
          })}
        />
        <Field
          label="Frequency"
          type="select"
          options={FREQUENCY_OPTIONS}
          placeholder="Select..."
          {...fieldAdapter({
            name: `financialDebtRepaymentFrequency_${liabilityIndex}`,
            condition,
          })}
        />
      </div>
      <div className="input-row">
        <div className="left">
          <Field
            type="radio-button"
            label="Is this an investment property?"
            options={["Yes", "No"]}
            {...fieldAdapter({
              name: `financialDebtIsInvestment_${liabilityIndex}`,
              condition,
            })}
          />
        </div>
        <div className="right">
          {!isJoint && (
            <Field
              type="radio-button"
              label="Is this a joint loan?"
              options={["Yes", "No"]}
              {...fieldAdapter({
                name: `financialDebtIsJointLoan_${liabilityIndex}`,
                condition: condition && !isJoint,
              })}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const getLiabilityFieldSet = (
  fieldAdapter: UseFormTools<Serviceability>["fieldAdapter"],
  setValue: UseFormTools<Serviceability>["setValue"],
  formData: UseFormTools<Serviceability>["formData"],
  liabilityIndex: number,
  liabilitiesLength: number,
  setLiabilitiesLength: (v: number) => void,
  debtTypeOptions: FormValuesLabels["debtTypes"],
  isJoint: boolean
) => {
  const liabilitiesFieldHidden = liabilityIndex > liabilitiesLength;

  return (
    <div
      key={liabilityIndex}
      className={liabilitiesFieldHidden ? "hidden" : "liability-field-set"}
    >
      <style jsx>{style}</style>
      <style jsx>{liabilityStyle}</style>
      <div className="debt-count">
        <span>Debt {liabilityIndex}</span>
        <div className="delete-icon">
          <a
            onClick={() => {
              removeLiability(
                formData,
                setValue,
                liabilityIndex,
                liabilitiesLength,
                setLiabilitiesLength
              );
            }}
          >
            <Icon name="action-trash-outline" size="sm" />
          </a>
        </div>
      </div>

      <div className="input-row">
        <Field
          type="select"
          label="Debt type"
          options={debtTypeOptions}
          placeholder="Select..."
          {...fieldAdapter({
            name: `financialDebtType_${liabilityIndex}`,
            condition: !liabilitiesFieldHidden,
          })}
        />
        <div className="right" />
      </div>
      {creditCardField(
        fieldAdapter,
        liabilityIndex,
        !liabilitiesFieldHidden &&
          formData[`financialDebtType_${liabilityIndex}`] ===
            DebtType.CREDIT_CARD
      )}
      {personalLoanField(
        fieldAdapter,
        liabilityIndex,
        !liabilitiesFieldHidden &&
          formData[`financialDebtType_${liabilityIndex}`] ===
            DebtType.PERSONAL_LOAN,
        isJoint
      )}
      {mortgageField(
        fieldAdapter,
        liabilityIndex,
        !liabilitiesFieldHidden &&
          formData[`financialDebtType_${liabilityIndex}`] === DebtType.MORTGAGE,
        isJoint
      )}
    </div>
  );
};

export const getLiabilitiesList = (
  fieldAdapter: UseFormTools<Serviceability>["fieldAdapter"],
  setValue: UseFormTools<Serviceability>["setValue"],
  formData: UseFormTools<Serviceability>["formData"],
  liabilitiesLength: number,
  maxLiabilitiesLength: number,
  setLiabilitiesLength: (v: number) => void,
  debtTypeOptions: FormValuesLabels["debtTypes"],
  isJoint: boolean
) => {
  const liabilitiesFieldSets: JSX.Element[] = [];
  if (maxLiabilitiesLength > 0) {
    for (let i = 1; i <= maxLiabilitiesLength; i++) {
      liabilitiesFieldSets.push(
        getLiabilityFieldSet(
          fieldAdapter,
          setValue,
          formData,
          i,
          liabilitiesLength,
          setLiabilitiesLength,
          debtTypeOptions,
          isJoint
        )
      );
    }
    return liabilitiesFieldSets;
  }
  return null;
};
